import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row } from '@/lib/supabase'

export type Communication = Row<'communications'> & {
  job: { id: string; job_number: number; kind: Row<'jobs'>['kind']; title: string | null; event_date: string; primary_contact: { first_name: string; last_name: string | null } | null; partner_contact: { first_name: string; last_name: string | null } | null } | null
  drafts: Array<Row<'comm_drafts'>>
}
export type CommDraft = Row<'comm_drafts'>

export const commKeys = {
  all: ['communications'] as const,
  forJob: (jobId: string) => ['communications', 'job', jobId] as const,
  sync: ['comm-sync'] as const,
  drafts: ['comm-drafts'] as const,
}

const SELECT =
  '*, job:jobs(id, job_number, kind, title, event_date, primary_contact:contacts!jobs_primary_contact_id_fkey(first_name, last_name), partner_contact:contacts!jobs_partner_contact_id_fkey(first_name, last_name)), drafts:comm_drafts(*)'

export function useCommunications() {
  return useQuery({
    queryKey: commKeys.all,
    queryFn: async (): Promise<Communication[]> => {
      const { data, error } = await supabase.from('communications').select(SELECT).order('last_message_at', { ascending: false }).limit(300)
      if (error) throw error
      return data as unknown as Communication[]
    },
  })
}

export function useJobCommunications(jobId: string) {
  return useQuery({
    queryKey: commKeys.forJob(jobId),
    queryFn: async (): Promise<Communication[]> => {
      const { data, error } = await supabase.from('communications').select(SELECT).eq('job_id', jobId).order('last_message_at', { ascending: false })
      if (error) throw error
      return data as unknown as Communication[]
    },
  })
}

export function useCommSync() {
  return useQuery({
    queryKey: commKeys.sync,
    queryFn: async (): Promise<Array<Row<'comm_sync'>>> => {
      const { data, error } = await supabase.from('comm_sync').select('*')
      if (error) throw error
      return data
    },
  })
}

/** Drafts the pipeline has written and she has not dealt with yet. */
/** The latest brief from the morning or evening check. */
export function useLatestBrief() {
  return useQuery({
    queryKey: ['daily-brief'],
    queryFn: async (): Promise<Row<'daily_briefs'> | null> => {
      const { data, error } = await supabase.from('daily_briefs').select('*').order('created_at', { ascending: false }).limit(1).maybeSingle()
      if (error) throw error
      return data
    },
  })
}

export function useOpenDrafts() {
  return useQuery({
    queryKey: commKeys.drafts,
    queryFn: async (): Promise<CommDraft[]> => {
      const { data, error } = await supabase.from('comm_drafts').select('*').in('status', ['requested', 'drafted', 'placed']).order('created_at')
      if (error) throw error
      return data
    },
  })
}

function useInvalidate() {
  const queryClient = useQueryClient()
  return () => void queryClient.invalidateQueries({ queryKey: ['communications'] }).then(() => queryClient.invalidateQueries({ queryKey: commKeys.drafts }))
}

export function useUpdateCommunication() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Row<'communications'>> }) => {
      const { error } = await supabase.from('communications').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save', { description: describeError(error) }),
  })
}

/** Asks the pipeline for a draft. It picks requests up on its next pass. */
export function useRequestDraft() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (input: {
      communication: Pick<Row<'communications'>, 'id' | 'job_id' | 'channel' | 'counterpart_name' | 'counterpart_address' | 'subject'>
      instructions: string
      staffId: string
    }) => {
      const { error } = await supabase.from('comm_drafts').insert({
        communication_id: input.communication.id,
        job_id: input.communication.job_id,
        channel: input.communication.channel,
        to_name: input.communication.counterpart_name,
        to_address: input.communication.counterpart_address,
        subject: input.communication.subject ? `Re: ${input.communication.subject.replace(/^re:\s*/i, '')}` : null,
        instructions: input.instructions.trim() || null,
        requested_by: input.staffId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      invalidate()
      toast.success('Draft requested', { description: 'It will be ready after the next check.' })
    },
    onError: (error) => toast.error('Could not request a draft', { description: describeError(error) }),
  })
}

export function useUpdateDraft() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<CommDraft> }) => {
      const { error } = await supabase.from('comm_drafts').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save the draft', { description: describeError(error) }),
  })
}

/** Gmail's compose window, in the business mailbox. Jena reads it and presses Send. */
export function gmailCompose(to: string, subject: string, body: string): string {
  const params = new URLSearchParams({
    authuser: 'info.deleeuwco@gmail.com',
    view: 'cm',
    fs: '1',
    to,
    su: subject,
    body,
  })
  return `https://mail.google.com/mail/?${params.toString()}`
}

/** wa.me wants the number in international form, digits only. SA numbers start 0. */
export function whatsAppLink(phone: string | null, text: string | null): string | null {
  if (!phone) return null
  let digits = phone.replace(/\D/g, '')
  if (digits.startsWith('0')) digits = `27${digits.slice(1)}`
  if (digits.length < 9) return null
  return `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ''}`
}
